import _ from 'lodash';
import log4javascript from 'log4javascript';
import moment from 'moment-timezone';
import { HTTP_MAX_BODY_SIZE_BYTES, TEST_ENV } from '@/main/app.constants';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { isClientLogEnabled } from '@/services/systemConfiguration.utilities';
import { emitLog, getCommonAttributes, serializeError } from '@seeqdev/telemetry';

export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

/** Structured attributes for an off-box log record; mirrors the OTel log-record attribute type. */
export type LogAttributes = NonNullable<Parameters<typeof emitLog>[2]>;

export const LOG_ROOT_NAME = 'sqLogger';

/**
 * Logs a message to the browser console at the specified level.
 *
 * @param message - Message to log. Note that a message longer than HTTP_MAX_BODY_SIZE_BYTES will be
 *   truncated when POSTed via HTTP.
 * @param [category] - A category that will allow log messages to be grouped (e.g. chart)
 */
export function logToConsole(level: LogLevel, message: string | Error, category?: string): [string, string] {
  const formattedMessage = formatValue(message);
  const timestamp = moment().format('HH:mm:ss.SSS');
  const loggerName = _.isUndefined(category) ? LOG_ROOT_NAME : `${LOG_ROOT_NAME}.${category}`;

  // Don't log to the console in test mode since it will be captured by jest-fail-on-console and cause test failures
  if (process.env.NODE_ENV !== TEST_ENV) {
    console[level === 'fatal' ? 'error' : level](`${timestamp} [${loggerName}] ${formattedMessage}`);
  }

  return [formattedMessage, loggerName];
}

/**
 * Logs a message to the browser console and ships it off-box at the specified level.
 *
 * Two transports run in parallel during the OTel RUM validation window:
 *   - the legacy log4javascript AjaxAppender (POST to /client-log → nginx → Loki), gated by
 *     the Features/Telemetry/Webserver/LegacyClientLog/Enabled kill-switch (on by default), and
 *   - OTel Logs (`emitLog` → /otel/v1/logs → Collector → Loki).
 * The OTel path is a no-op until the telemetry SDK is initialized (i.e. when
 * Features/Telemetry/Webserver/Enabled is on), so it adds nothing when disabled. Once OTel/Loki
 * parity is verified for a deployment, turn the kill-switch off to retire the legacy transport.
 *
 * The legacy transport stays skipped in the headless renderer, as it always has been. The OTel record
 * is emitted there: scheduled content captures are where stale saved state most often fails to
 * render, and without it those failures would never leave the browser.
 *
 * @param message - Message to log. Note that a message longer than HTTP_MAX_BODY_SIZE_BYTES will be
 *   truncated when POSTed via the legacy HTTP transport (the OTel record carries the full message).
 * @param [category] - A category that will allow log messages to be grouped (e.g. chart)
 * @param [attributes] - Structured attributes attached to the OTel record (queryable in Loki). The
 *   legacy transport carries the message text only, so anything that must be grep-able there needs
 *   to be in the message as well.
 */
export function log(level: LogLevel, message: string | Error, category?: string, attributes?: LogAttributes) {
  const [msg, loggerName] = logToConsole(level, message, category);

  if (!headlessRenderMode() && isClientLogEnabled()) {
    const logger = log4javascript.getLogger(loggerName);
    const ellipses = msg.length > HTTP_MAX_BODY_SIZE_BYTES ? ' ...[truncated]' : '';
    logger[level](msg.slice(0, HTTP_MAX_BODY_SIZE_BYTES) + ellipses);
  }

  emitLog(level, msg, { ...getCommonAttributes(), ...attributes, 'logger.name': loggerName });
}

/**
 * Formats the given Error in a format suitable for logging
 *
 * @param error - the Error to format as a string
 * @param includeStack - include the stack trace or not to produce the formatted message
 */
export function formatError(error: Error, includeStack = false): string {
  if (!includeStack) {
    return error.message;
  }
  return formatValue(error);
}

/**
 * Formats the value in a format suitable for logging with the most information. Delegates to the
 * canonical telemetry serializer (`@seeqdev/telemetry`'s `serializeError`), which is extended with
 * Seeq-specific recognizers (http responses/configs, websocket status messages) and
 * sensitive-data scrubbers by an explicit `registerSeeqTelemetryErrorHandlers()` call made at app
 * bootstrap (`index.tsx`), not by this module.
 *
 * @param value - the value to format as a string
 */
export function formatValue(value: any): string {
  return serializeError(value);
}

/**
 * Tagged template literal, formats inputs suitable for logging with the most information
 *
 * @example format `Error with ${object}: ${ex}`
 * @param strings - array built of the string components of the template (ex: ['Error with ', ': ', ''])
 * @param values - array built of the value components of the template (ex: [object, ex])
 */
export function formatMessage(strings: TemplateStringsArray, ...values: unknown[]) {
  const result: string[] = [];
  for (let x = 0; x < strings.length; x++) {
    if (x !== 0) {
      result.push(formatValue(values[x - 1]));
    }

    result.push(strings[x]);
  }

  return _.join(result, '');
}
